import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { US_STATES } from "./OpenStorePage";
import { SEOExpansion } from "@/components/shared/SEOExpansion";

export default function OpenStoreListPage() {
  const stateEntries = Object.entries(US_STATES).sort((a, b) => a[1].name.localeCompare(b[1].name));

  return (
    <PublicLayout>
      <SEOHead
        pageSlug="open-a-store"
        fallback={{
          meta_title: "Total Wireless Master Agent & Dealer Opportunities | Prime Media Wireless",
          meta_description: "Become a Total Wireless dealer with Prime Media Wireless. Get help opening your wireless store, dealer training, competitive commissions, and nationwide support.",
        }}
      />

      {/* Hero */}
      <section className="bg-navy py-20" data-testid="open-store-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">Dealer Opportunities</p>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight">
              Become a Total Wireless Dealer — Open or Expand Your Cell Phone Store
            </h1>
            <p className="text-slate-300 text-base lg:text-lg font-body">
              Partner with Prime Media Wireless, a Total Wireless Master Agent helping entrepreneurs launch, convert,
              and grow wireless retail businesses nationwide.
            </p>
          </div>
        </div>
      </section>

      {/* State Grid */}
      <section className="py-16 bg-white" data-testid="open-store-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {stateEntries.map(([slug, data], i) => (
              <motion.div
                key={slug}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.02 }}
              >
                <Link
                  to={`/${slug}`}
                  className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-teal hover:shadow-md transition-all group"
                  data-testid={`state-link-${slug}`}
                >
                  <MapPin className="w-4 h-4 text-slate-400 group-hover:text-teal transition-colors flex-shrink-0" />
                  <span className="font-body text-sm text-slate-700 group-hover:text-teal transition-colors">{data.name}</span>
                  <ArrowRight className="w-3 h-3 text-slate-300 group-hover:text-teal ml-auto transition-colors flex-shrink-0" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50" data-testid="open-store-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-4">Ready to Get Started?</h2>
          <p className="text-slate-600 font-body text-base mb-8">
            Select your state above to learn more, or apply directly to become a dealer.
          </p>
          <Link
            to="/become-dealer"
            className="inline-flex items-center gap-2 bg-teal text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-teal/90 transition-colors"
            data-testid="open-store-apply-cta"
          >
            Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <SEOExpansion />
    </PublicLayout>
  );
}
